import { abs } from "~/tools/abs";
import { clamp } from "~/tools/clamp";
import { floor } from "~/tools/floor";
import { max } from "~/tools/max";
import { pow } from "~/tools/pow";
import { logx } from "~/tools/trigonometry";

import type { UnitFormatterWithOptions } from "./_types";
import { toFixed } from "./_value-formats";

export const scaledUnits =
	(factor: number, extArray: Array<string>, offset = 0): UnitFormatterWithOptions =>
	(size, { precision } = {}) => {
		if (!isFinite(size) || isNaN(size)) {
			return size.toLocaleString();
		}

		const siIndex = size === 0 ? 0 : floor(logx(factor, abs(size)), 0);
		const unitIndex = clamp(offset + siIndex, { min: 0, max: extArray.length - 1 });
		const scale = max(0, unitIndex - offset);
		const value = toFixed(size / pow(factor, scale), precision);
		const ext = extArray[unitIndex];

		return `${value}${ext}`;
	};

const CURRENCY_UNITS = ["", "K", "M", "B", "T"];

const currencyScaler = scaledUnits(1000, CURRENCY_UNITS);

export const currency =
	(symbol: string, asSuffix?: boolean): UnitFormatterWithOptions =>
	(size, options) => {
		const scaled = currencyScaler(size, options);

		return asSuffix ? `${scaled}${symbol}` : `${symbol}${scaled}`;
	};

const SI_PREFIXES = ["f", "p", "n", "µ", "m", "", "k", "M", "G", "T", "P", "E", "Z", "Y"];
const SI_BASE_INDEX = SI_PREFIXES.indexOf("");

const BIN_PREFIXES = ["", "Ki", "Mi", "Gi", "Ti", "Pi", "Ei", "Zi", "Yi"];

export const binaryPrefix = (unit: string, offset = 0): ReturnType<typeof scaledUnits> => {
	const units = BIN_PREFIXES.map((prefix) => ` ${prefix}${unit}`);
	return scaledUnits(1024, units, offset);
};

export const siPrefix = (unit: string, offset = 0): ReturnType<typeof scaledUnits> => {
	const units = SI_PREFIXES.map((prefix) => ` ${prefix}${unit}`);
	return scaledUnits(1000, units, SI_BASE_INDEX + offset);
};

const SIMPLE_UNITS = ["", "K", "M", "B", "T"];
const simpleUnitsScaler = scaledUnits(1000, SIMPLE_UNITS);

export const simpleCountUnit =
	(symbol: string): UnitFormatterWithOptions =>
	(size, options = {}) =>
		`${simpleUnitsScaler(size, options)} ${symbol}`;

export const shortUnitsScaler = scaledUnits(1000, [
	"",
	" K",
	" Mil",
	" Bil",
	" Tri",
	" Quadr",
	" Quint",
	" Sext",
	" Sept",
]);

export const pressureUnitsScaler = scaledUnits(1000, ["psi", "ksi", "Mpsi"]);
